import type {
  JobCreateRequest,
  Product,
  Term,
  Stage,
} from "@acs/shared";

const BASE = (import.meta.env.VITE_API_BASE ?? "/api").replace(/\/$/, "");

export const TOKEN_KEY = "acs_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string): void {
  localStorage.setItem(TOKEN_KEY, t);
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { headers, ...init });

  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("Oturum sona erdi.");
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  login: (email: string, password: string) =>
    http<{ ok: boolean; token: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  health: () => http<{ ok: boolean; env: string }>("/health"),

  // Products
  listProducts: () => http<{ ok: boolean; products: Product[] }>("/products"),
  getProduct: (id: string) =>
    http<{ ok: boolean; product: Product & { types: string[] } }>(
      `/products/${id}`,
    ),
  createProduct: (body: {
    name: string;
    tag: string;
    drive_folder_id: string;
    types?: string[];
  }) =>
    http<{ ok: boolean; id: string }>("/products", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateProduct: (
    id: string,
    body: { name?: string; drive_folder_id?: string; types?: string[] },
  ) =>
    http<{ ok: boolean }>(`/products/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteProduct: (id: string) =>
    http<{ ok: boolean }>(`/products/${id}`, { method: "DELETE" }),

  // Terms
  listTerms: (productId: string) =>
    http<{ ok: boolean; terms: Term[] }>(`/terms?product_id=${productId}`),
  createTerm: (productId: string, name: string) =>
    http<{ ok: boolean; id: string }>("/terms", {
      method: "POST",
      body: JSON.stringify({ product_id: productId, name }),
    }),
  deleteTerm: (id: string) =>
    http<{ ok: boolean }>(`/terms/${id}`, { method: "DELETE" }),

  // Stages
  listStages: () => http<{ ok: boolean; stages: Stage[] }>("/stages"),

  // Jobs
  createJob: (body: JobCreateRequest) =>
    http<{ ok: boolean; id: string }>("/jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listJobs: (productId?: string) =>
    http<{ ok: boolean; jobs: any[] }>(
      `/jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  getJob: (id: string) => http<{ ok: boolean; job: any; media: any[]; files: any[]; costs: any[] }>(`/jobs/${id}`),
  cancelJob: (id: string) =>
    http<{ ok: boolean }>(`/jobs/${id}/cancel`, { method: "POST" }),
  createEditJob: (body: {
    product_id: string;
    codes: string[];
    operation: "translate" | "resize" | "video";
    languages?: string[];
    sizes?: string[];
    video_duration?: number;
    video_size?: string;
  }) =>
    http<{ ok: boolean; id: string }>("/edit-jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Edit jobs
  listEditJobs: (productId?: string) =>
    http<{ ok: boolean; edit_jobs: any[] }>(
      `/edit-jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  cancelEditJob: (id: string) =>
    http<{ ok: boolean }>(`/edit-jobs/${id}/cancel`, { method: "POST" }),

  // Media
  listMedia: (productId?: string) =>
    http<{ ok: boolean; media: any[] }>(
      `/media${productId ? `?product_id=${productId}` : ""}`,
    ),

  // Settings
  getSettings: () =>
    http<{ ok: boolean; keys: Record<string, boolean>; drive_editor_email: string }>(
      "/settings",
    ),
};
